// @ts-nocheck
// tool-shopify-plan-picker.js — Shopify Plan Picker behaviour (SSM-636).
//
// Cost math and rate data come from window.SheroPlans (assets/tool-shopify-plans.js).
// What lives here is what the picker adds on top: gateway-aware totals, the
// distance-to-next-crossover read, the cost-health band, and the Plus-fit score.
// Ported from src/lib/shopifyPlanPicker.ts.
//
// TWO STRUCTURAL RULES, both load-bearing:
//   1. The Plus-fit score is computed from the four checkboxes and revenue ONLY, and its
//      result is read only by the fit card, the upsell banner and the clipboard summary.
//      It is never passed into any function that returns money.
//   2. No email gate. The comparison table renders as soon as there is a result.
//
// Unlike the other tools, this one SNAPSHOTS its result rather than live-recomputing:
// the result view does not react to input changes, and getting back to it goes through
// the wizard. That is deliberate and matches the original.
(function () {
  var roots = document.querySelectorAll('[data-tool="plan_picker"]');
  if (!roots.length) return;

  var P = window.SheroPlans;
  if (!P) return;

  var FIT_SIGNALS = ['b2b', 'checkout', 'intl', 'automation'];
  var FIT_SCALE_THRESHOLD = 500000;
  var FIT_MAX_SCORE = FIT_SIGNALS.length + 1;
  var PLUS_FEATURE_THRESHOLD = 1000000;
  var HEALTH_WARN_PCT = 4;
  var HEALTH_BAD_PCT = 6.5;
  var TOTAL_STEPS = 6;

  function usd(n) {
    return '$' + Math.round(n).toLocaleString('en-US');
  }

  function pct(n) {
    return n.toFixed(1) + '%';
  }

  // Note this parser differs from the other tools: it strips only whitespace, commas
  // and dollar signs, so "85,000", "$85,000" and "85 000" parse but "85k" does not.
  // Kept distinct on purpose — parity is the acceptance test.
  function parseNum(raw) {
    var cleaned = String(raw).replace(/[\s,$]/g, '');
    if (cleaned === '') return null;
    var n = Number(cleaned);
    return isFinite(n) && n >= 0 ? n : null;
  }

  function isPlanKey(v) {
    return P.PLAN_KEYS.indexOf(v) !== -1;
  }

  function mount(root) {
    if (root.dataset.toolMounted) return;
    root.dataset.toolMounted = 'true';

    var wizard = root.querySelector('[data-spp-wizard]');
    var resultView = root.querySelector('[data-spp-result]');
    var stepNodes = root.querySelectorAll('[data-spp-step]');
    var errorSlot = root.querySelector('[data-spp-error]');
    var backBtn = root.querySelector('[data-spp-back]');
    var skipBtn = root.querySelector('[data-spp-skip]');
    var nextBtn = root.querySelector('[data-spp-next]');
    if (!wizard || !resultView || !stepNodes.length || !nextBtn) return;

    var out = {};
    root.querySelectorAll('[data-spp-out]').forEach(function (el) {
      out[el.getAttribute('data-spp-out')] = el;
    });

    var fields = {};
    root.querySelectorAll('[data-spp-input]').forEach(function (el) {
      fields[el.getAttribute('data-spp-input')] = el;
    });

    var gatewayBtns = root.querySelectorAll('[data-spp-gateway]');
    var fitBoxes = root.querySelectorAll('[data-spp-fit]');

    var step = 1;
    var gateway = P.DEFAULT_GATEWAY;
    var snapshot = null;
    var copyTimer = null;

    function set(key, value) {
      if (out[key]) out[key].textContent = value;
    }

    function showOnly(selector, attr, activeKey) {
      root.querySelectorAll(selector).forEach(function (el) {
        el.hidden = el.getAttribute(attr) !== activeKey;
      });
    }

    function toggle(selector, visible) {
      root.querySelectorAll(selector).forEach(function (el) {
        el.hidden = !visible;
      });
    }

    // ── Picker math (src/lib/shopifyPlanPicker.ts) ─────────────────────────
    function pricePlans(input) {
      var gw = input.gateway || P.DEFAULT_GATEWAY;
      var appSpend = input.appSpend || 0;
      var period = P.DEFAULT_PERIOD;
      var hasInputs = input.revenue > 0 && input.aov > 0;
      var orders = P.calcOrders(input.revenue, input.aov);

      var rows = P.PLAN_KEYS.map(function (key) {
        var config = P.planFor(key);
        var sub = P.subFor(key, period).amount;
        // Through effectiveRate, never rate + surcharge re-derived locally, so this and
        // breakEvenRevenue can never disagree. The two are rendered side by side.
        var procTotal = hasInputs
          ? input.revenue * P.effectiveRate(key, gw) + orders * config.perOrder
          : 0;
        var total = sub + procTotal;
        return {
          key: key,
          name: config.name,
          sub: sub,
          rateIsEstimate: config.rateIsEstimate,
          procTotal: procTotal,
          total: total,
          allIn: total + appSpend,
          // Apps excluded, so this is comparable across stores.
          pctOfRevenue: hasInputs ? (total / input.revenue) * 100 : 0
        };
      });

      // Cheapest on `total`, never `allIn`: app spend is identical on every row.
      // Strict `<` in PLAN_KEYS order, so a tie goes to the cheaper subscription.
      var cheapestKey = rows[0].key;
      var cheapestTotal = rows[0].total;
      for (var i = 1; i < rows.length; i++) {
        if (rows[i].total < cheapestTotal) {
          cheapestKey = rows[i].key;
          cheapestTotal = rows[i].total;
        }
      }

      return { orders: orders, hasInputs: hasInputs, cheapestKey: cheapestKey, rows: rows, appSpend: appSpend };
    }

    function crossoverFrom(cheapestKey, revenue, gw) {
      var index = P.PLAN_KEYS.indexOf(cheapestKey);
      var cheapName = P.planFor(cheapestKey).name;

      if (index === P.PLAN_KEYS.length - 1) {
        return { variant: 'top', label: 'Top tier' };
      }

      var nextKey = P.PLAN_KEYS[index + 1];
      var nextName = P.planFor(nextKey).name;

      if (nextKey === 'plus') {
        return revenue * 12 > PLUS_FEATURE_THRESHOLD
          ? { variant: 'plus-feature', label: 'Feature-driven' }
          : { variant: 'plus-notyet', label: 'Not yet' };
      }

      var crossover = P.breakEvenRevenue(cheapestKey, nextKey, P.DEFAULT_PERIOD, gw);
      if (crossover === null) {
        return { variant: 'none', label: 'No crossover', nextName: nextName, cheapName: cheapName };
      }

      // Round BEFORE branching: the crossover is a float division, so exactly on a
      // threshold the residue is ~1e-11 and a raw `> 0` test renders "$0 more GMV/mo".
      // And at the threshold the plans are tied, which pricePlans resolves toward the
      // cheaper subscription — so "past crossover" would contradict the plan named above.
      var distance = Math.round(crossover - revenue);

      if (distance > 0) {
        return {
          variant: 'distance',
          label: '$' + distance.toLocaleString('en-US') + ' more GMV/mo',
          nextName: nextName,
          cheapName: cheapName
        };
      }
      if (distance === 0) {
        return { variant: 'at', label: 'At the crossover', nextName: nextName, cheapName: cheapName };
      }
      return { variant: 'past', label: 'Past crossover', nextName: nextName, cheapName: cheapName };
    }

    function healthBand(pctOfRevenue) {
      // Band the figure the UI shows, not the raw one, so the colour and the number
      // can never disagree.
      var shown = Math.round(pctOfRevenue * 10) / 10;
      if (shown >= HEALTH_BAD_PCT) return 'bad';
      if (shown >= HEALTH_WARN_PCT) return 'warn';
      return 'good';
    }

    function overpayVsCheapest(comparison, currentPlanKey) {
      if (!currentPlanKey || currentPlanKey === comparison.cheapestKey) return null;
      // Leaving Plus is not an overpay finding; plusCostGap reports that instead.
      if (P.isPlusDowngrade(currentPlanKey, comparison.cheapestKey)) return null;
      var current = comparison.rows.filter(function (r) { return r.key === currentPlanKey; })[0];
      var cheapest = comparison.rows.filter(function (r) { return r.key === comparison.cheapestKey; })[0];
      if (!current || !cheapest) return null;
      var perMonth = current.total - cheapest.total;
      if (perMonth <= 1) return null;
      return { perMonth: perMonth, perYear: perMonth * 12, currentName: current.name };
    }

    function plusCostGap(comparison, currentPlanKey) {
      if (!currentPlanKey || !P.isPlusDowngrade(currentPlanKey, comparison.cheapestKey)) return null;
      var current = comparison.rows.filter(function (r) { return r.key === currentPlanKey; })[0];
      var cheapest = comparison.rows.filter(function (r) { return r.key === comparison.cheapestKey; })[0];
      if (!current || !cheapest) return null;
      var perMonth = current.total - cheapest.total;
      if (perMonth <= 1) return null;
      return { perMonth: perMonth, lowerName: cheapest.name };
    }

    // Feature need only. Takes the checkboxes and revenue, nothing else, and is called
    // last so it cannot feed any cost figure.
    function computePlusFit(checks, revenue) {
      var reasons = FIT_SIGNALS.filter(function (k) { return checks[k]; });
      var annualised = revenue * 12;
      var score = reasons.length + (annualised >= FIT_SCALE_THRESHOLD ? 1 : 0);

      var tier = 'low';
      var tierLabel = 'Low fit for now';
      if (score >= 2 && score <= 3) { tier = 'mid'; tierLabel = 'Worth exploring'; }
      if (score >= 4) { tier = 'high'; tierLabel = 'Strong fit'; }

      return {
        score: score, maxScore: FIT_MAX_SCORE, tier: tier, tierLabel: tierLabel,
        reasons: reasons, annualised: annualised, showUpsell: tier !== 'low'
      };
    }

    // ── Wizard ─────────────────────────────────────────────────────────────
    function stepNode(n) {
      return root.querySelector('[data-spp-step="' + n + '"]');
    }

    function showStep(n, focus) {
      step = n;
      stepNodes.forEach(function (el) {
        el.hidden = Number(el.getAttribute('data-spp-step')) !== n;
      });
      var active = stepNode(n);
      set('step', String(n));
      if (active) set('tag', active.getAttribute('data-spp-tag') || '');
      if (out.progress) out.progress.style.width = (n / TOTAL_STEPS) * 100 + '%';
      if (backBtn) backBtn.hidden = n === 1;
      // Skip only on optional steps, and never on the last one, so there is no
      // skip-to-result path: step 6 always goes through "See my result".
      var optional = active && active.getAttribute('data-spp-required') !== 'true';
      if (skipBtn) skipBtn.hidden = !(optional && n < TOTAL_STEPS);
      nextBtn.textContent = n === TOTAL_STEPS ? 'See my result' : 'Next';

      if (focus && active) {
        var input = active.querySelector('input[type="text"], select');
        if (input) input.focus({ preventScroll: true });
      }
    }

    function setError(msg) {
      if (errorSlot) errorSlot.textContent = msg;
    }

    function readChecks() {
      var checks = {};
      fitBoxes.forEach(function (cb) {
        checks[cb.getAttribute('data-spp-fit')] = cb.checked;
      });
      return checks;
    }

    function goNext() {
      var active = stepNode(step);
      var key = active && active.getAttribute('data-spp-key');
      var required = active && active.getAttribute('data-spp-required') === 'true';

      if (required && fields[key]) {
        var parsed = parseNum(fields[key].value);
        if (parsed === null || parsed <= 0) {
          setError('Enter an amount greater than 0 to continue.');
          return;
        }
      }
      setError('');

      if (step === TOTAL_STEPS) {
        buildResult();
        return;
      }
      showStep(step + 1, true);
    }

    function skipStep() {
      var active = stepNode(step);
      var key = active && active.getAttribute('data-spp-key');
      // Clear the half-entered value rather than carrying it forward.
      if (key && fields[key]) fields[key].value = '';
      setError('');
      showStep(step + 1, true);
    }

    // ── Result ─────────────────────────────────────────────────────────────
    function buildResult() {
      var revenue = parseNum(fields.gmv.value);
      var aov = parseNum(fields.aov.value);
      // Re-validate rather than trusting the step gate.
      if (revenue === null || aov === null || revenue <= 0 || aov <= 0) {
        setError('Enter an amount greater than 0 to continue.');
        showStep(revenue === null || revenue <= 0 ? 1 : 2, true);
        return;
      }

      var appSpend = parseNum(fields.appSpend.value) || 0;
      var currentRaw = fields.currentPlan.value;
      var currentPlan = isPlanKey(currentRaw) ? currentRaw : null;
      var checks = readChecks();

      var comparison = pricePlans({ revenue: revenue, aov: aov, gateway: gateway, appSpend: appSpend });
      var cheapest = comparison.rows.filter(function (r) { return r.key === comparison.cheapestKey; })[0];
      var crossover = crossoverFrom(comparison.cheapestKey, revenue, gateway);
      var band = healthBand(cheapest.pctOfRevenue);
      var overpay = overpayVsCheapest(comparison, currentPlan);
      var plusGap = plusCostGap(comparison, currentPlan);
      var fit = computePlusFit(checks, revenue);

      // `appSpend > 0`, not "field non-empty": an explicit 0 behaves exactly like blank.
      var appsProvided = appSpend > 0;

      snapshot = {
        revenue: revenue, aov: aov, orders: comparison.orders, gateway: gateway,
        appSpend: appSpend, appsProvided: appsProvided, comparison: comparison,
        cheapest: cheapest, crossover: crossover, band: band, overpay: overpay,
        plusGap: plusGap, fit: fit, currentPlan: currentPlan, checks: checks
      };

      renderResult();
      wizard.hidden = true;
      resultView.hidden = false;
      resultView.classList.add('tool__reveal');
    }

    function renderResult() {
      var s = snapshot;
      if (!s) return;

      set('cheapestName', s.cheapest.name);
      set('cheapestName2', s.cheapest.name);
      set('headlineCost', usd(s.appsProvided ? s.cheapest.allIn : s.cheapest.total));
      set('revenue', usd(s.revenue));
      set('aov', usd(s.aov));
      set('orders', s.orders.toLocaleString('en-US'));
      set('gatewayLabel', s.gateway === 'third-party' ? 'a third-party gateway' : 'Shopify Payments');
      set('cheapestTotal', usd(s.cheapest.total));
      set('appSpend', usd(s.appSpend));
      set('appSpend2', usd(s.appSpend));
      toggle('[data-spp-apps-clause]', s.appsProvided);
      toggle('[data-spp-apps-clause2]', s.appsProvided);
      toggle('[data-spp-thirdparty]', s.gateway === 'third-party');

      // Tile 1
      toggle('[data-spp-note="tile-allin"]', s.appsProvided);
      toggle('[data-spp-note="tile-platform"]', !s.appsProvided);
      set('tileCost', usd(s.appsProvided ? s.cheapest.allIn : s.cheapest.total));
      set('tileSub', usd(s.cheapest.sub));
      set('tileProc', usd(s.cheapest.procTotal));

      // Tile 2
      set('pct', pct(s.cheapest.pctOfRevenue));
      if (out.pct) out.pct.setAttribute('data-band', s.band);
      showOnly('[data-spp-health]', 'data-spp-health', s.band);

      // Tile 3
      set('crossoverLabel', s.crossover.label);
      showOnly('[data-spp-cross]', 'data-spp-cross', s.crossover.variant);
      ['crossNext1', 'crossNext2', 'crossNext3', 'crossNext4'].forEach(function (k) {
        set(k, s.crossover.nextName || '');
      });
      ['crossCheap1', 'crossCheap2', 'crossCheap3'].forEach(function (k) {
        set(k, s.crossover.cheapName || '');
      });

      // Callouts, mutually exclusive by construction.
      toggle('[data-spp-overpay]', !!s.overpay);
      if (s.overpay) {
        set('overpayName', s.overpay.currentName);
        set('overpayMonth', usd(s.overpay.perMonth));
        set('overpayYear', usd(s.overpay.perYear));
      }
      toggle('[data-spp-plusgap]', !!s.plusGap);
      if (s.plusGap) {
        set('plusLowerName', s.plusGap.lowerName);
        set('plusGapMonth', usd(s.plusGap.perMonth) + '/mo');
        set('plusSub', usd(P.PLANS.plus.sub));
      }

      // Plus fit
      set('fitBadge', s.fit.tierLabel + ' (' + s.fit.score + '/' + s.fit.maxScore + ')');
      if (out.fitBadge) out.fitBadge.setAttribute('data-tier', s.fit.tier);
      FIT_SIGNALS.forEach(function (k) {
        toggle('[data-spp-reason="' + k + '"]', s.fit.reasons.indexOf(k) !== -1);
      });
      toggle('[data-spp-reason="none"]', s.fit.reasons.length === 0);
      var scaleShown = s.fit.annualised >= FIT_SCALE_THRESHOLD;
      toggle('[data-spp-reason="scale"]', scaleShown);
      if (scaleShown) set('annualised', usd(s.fit.annualised));

      toggle('[data-spp-upsell]', s.fit.showUpsell);
      showOnly('[data-spp-upsell-copy]', 'data-spp-upsell-copy', s.fit.tier);

      // Comparison table. Rows always in PLAN_KEYS order, never sorted.
      toggle('[data-spp-apps-col]', s.appsProvided);
      s.comparison.rows.forEach(function (row) {
        var tr = root.querySelector('[data-spp-row="' + row.key + '"]');
        if (!tr) return;
        var c = {};
        tr.querySelectorAll('[data-spp-cell]').forEach(function (el) {
          c[el.getAttribute('data-spp-cell')] = el;
        });
        var isCheapest = row.key === s.comparison.cheapestKey;
        if (c.name) c.name.textContent = row.name;
        if (c.lowest) c.lowest.hidden = !isCheapest;
        if (c.estRate) c.estRate.hidden = !row.rateIsEstimate;
        if (c.sub) c.sub.textContent = usd(row.sub);
        if (c.proc) c.proc.textContent = usd(row.procTotal);
        if (c.apps) {
          c.apps.hidden = !s.appsProvided;
          c.apps.textContent = usd(s.appSpend);
        }
        if (c.total) c.total.textContent = usd(s.appsProvided ? row.allIn : row.total);
        if (c.pct) c.pct.textContent = pct(row.pctOfRevenue);
        tr.setAttribute('data-cheapest', isCheapest ? 'true' : 'false');
      });

      // Off-ladder plans: priced, never ranked.
      var simple = P.SIMPLE_PLANS[0];
      if (simple) {
        var cost = P.simplePlanCost(simple, s.revenue, s.aov);
        toggle('[data-spp-simple]', true);
        set(
          'simpleBody',
          simple.name + ', ' + usd(cost.total) + '/mo at your volume (' + usd(simple.sub) +
            '/mo plus ' + P.formatRate(simple.rate) + '%). ' + simple.positioning +
            ', with no online store of your own, so it is not ranked above.'
        );
        if (out.simpleLink) {
          out.simpleLink.setAttribute('href', simple.href);
          out.simpleLink.textContent = "Shopify's " + simple.name + ' plan';
        }
        set('simpleAsOf', P.SIMPLE_PLANS_AS_OF);
      }
    }

    // ── Static methodology figures ─────────────────────────────────────────
    function renderStatic() {
      set('perOrderFee', P.PER_ORDER_FEE.toFixed(2));
      set('ratesAsOf', P.RATES_AS_OF);
      set('warnPct', String(HEALTH_WARN_PCT));
      set('badPct', String(HEALTH_BAD_PCT));
      set('fitMax', String(FIT_MAX_SCORE));
      set('fitThreshold', usd(FIT_SCALE_THRESHOLD));
      set('plusThreshold', usd(PLUS_FEATURE_THRESHOLD));

      P.PLAN_KEYS.forEach(function (key) {
        var plan = P.planFor(key);
        var tr = root.querySelector('[data-spp-rate-row="' + key + '"]');
        if (!tr) return;
        var c = {};
        tr.querySelectorAll('[data-spp-rate]').forEach(function (el) {
          c[el.getAttribute('data-spp-rate')] = el;
        });
        if (c.name) c.name.textContent = plan.name;
        if (c.sub) c.sub.textContent = usd(plan.sub) + '/mo';
        if (c.cardRate) {
          c.cardRate.textContent =
            P.formatRate(plan.rate) + '%' + (plan.rateIsEstimate ? ' (estimate)' : '');
        }
        if (c.perOrder) c.perOrder.textContent = '$' + plan.perOrder.toFixed(2);
        if (c.surcharge) {
          c.surcharge.textContent = '+' + P.formatRate(P.THIRD_PARTY_SURCHARGE[key]) + '%';
        }
      });
    }

    // ── Share link ─────────────────────────────────────────────────────────
    // Read once on load, written once on click. Never touches history, so the address
    // bar does not change as the visitor progresses.
    function buildShareUrl() {
      var s = snapshot;
      if (!s) return '';
      var params = new URLSearchParams();
      params.set('gmv', String(s.revenue));
      params.set('aov', String(s.aov));
      params.set('gw', s.gateway);
      if (s.appsProvided) params.set('apps', String(s.appSpend));
      if (s.currentPlan) params.set('cur', s.currentPlan);
      FIT_SIGNALS.forEach(function (k) {
        if (s.checks[k]) params.set(k, '1');
      });
      return window.location.origin + window.location.pathname + '?' + params.toString();
    }

    function restoreFromUrl() {
      var params = new URLSearchParams(window.location.search);
      if (!params.has('gmv')) return;

      fields.gmv.value = params.get('gmv') || '';
      fields.aov.value = params.get('aov') || '';
      fields.appSpend.value = params.get('apps') || '';

      var gw = params.get('gw') === 'third-party' ? 'third-party' : 'shopify';
      setGateway(gw);

      var cur = params.get('cur') || '';
      fields.currentPlan.value = isPlanKey(cur) ? cur : '';

      fitBoxes.forEach(function (cb) {
        cb.checked = params.get(cb.getAttribute('data-spp-fit')) === '1';
      });

      // Only auto-compute when both required values are present and positive. A partial
      // link such as ?gmv=50000 with no aov would divide by zero and render a screen of
      // NaN, so it drops the visitor into the wizard with whatever restored.
      var revenue = parseNum(fields.gmv.value);
      var aov = parseNum(fields.aov.value);
      if (revenue === null || aov === null || revenue <= 0 || aov <= 0) return;
      buildResult();
    }

    function setGateway(next) {
      gateway = next;
      gatewayBtns.forEach(function (b) {
        b.setAttribute('aria-pressed', b.getAttribute('data-spp-gateway') === next ? 'true' : 'false');
      });
    }

    // ── Copy / print / reset ───────────────────────────────────────────────
    function flash(btn, label, message) {
      var original = btn.textContent;
      btn.textContent = label;
      btn.classList.add('tool__btn--copied');
      var statusEl = root.querySelector('[data-spp-status]');
      if (statusEl) statusEl.textContent = message;
      clearTimeout(copyTimer);
      copyTimer = setTimeout(function () {
        btn.textContent = original;
        btn.classList.remove('tool__btn--copied');
        if (statusEl) statusEl.textContent = '';
      }, 2500);
    }

    function copySummary(btn) {
      var s = snapshot;
      if (!s || !navigator.clipboard) return;
      var lines = [
        'Shopify Plan Picker (Shero Commerce)',
        'Monthly online GMV: ' + usd(s.revenue),
        'Average order value: ' + usd(s.aov) + ' (~' + s.orders.toLocaleString('en-US') + ' orders)',
        'Payments: ' + (s.gateway === 'third-party' ? 'Third-party gateway' : 'Shopify Payments'),
        'Lowest total cost: ' + s.cheapest.name,
        'Platform + processing: ' + usd(s.cheapest.total) + '/mo (' + pct(s.cheapest.pctOfRevenue) + ' of revenue)',
        s.appsProvided ? 'All-in with apps: ' + usd(s.cheapest.allIn) + '/mo' : 'App spend: not included',
        'Next crossover: ' + s.crossover.label,
        'Plus fit: ' + s.fit.tierLabel + ' (' + s.fit.score + '/' + s.fit.maxScore + ')'
      ];
      if (s.gateway === 'third-party') {
        lines.push("NOTE: figures cover Shopify's fees only. Your own gateway's processing rate is extra.");
      }
      if (s.overpay) {
        lines.push(
          'On ' + s.overpay.currentName + ' today: ' + usd(s.overpay.perMonth) +
            '/mo more than the lowest-cost plan (' + usd(s.overpay.perYear) + '/yr)'
        );
      }
      if (s.plusGap) {
        lines.push(
          'On Shopify Plus today: ' + s.plusGap.lowerName + ' is ' + usd(s.plusGap.perMonth) +
            '/mo less on processing math, but that is not the Plus decision. The Plus rate is an estimate and Plus is negotiated case by case.'
        );
      }
      navigator.clipboard.writeText(lines.join('\n')).then(function () {
        flash(btn, '✓ Copied!', 'Result summary copied to clipboard.');
      });
    }

    function backToWizard(reset) {
      if (reset) {
        Object.keys(fields).forEach(function (k) {
          if (fields[k].tagName === 'SELECT') fields[k].value = '';
          else fields[k].value = '';
        });
        fitBoxes.forEach(function (cb) { cb.checked = false; });
        setGateway(P.DEFAULT_GATEWAY);
        snapshot = null;
      }
      setError('');
      resultView.hidden = true;
      resultView.classList.remove('tool__reveal');
      wizard.hidden = false;
      showStep(1, false);
    }

    // ── Wiring ─────────────────────────────────────────────────────────────
    nextBtn.addEventListener('click', goNext);
    if (backBtn) backBtn.addEventListener('click', function () {
      setError('');
      if (step > 1) showStep(step - 1, true);
    });
    if (skipBtn) skipBtn.addEventListener('click', skipStep);

    gatewayBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        setGateway(btn.getAttribute('data-spp-gateway'));
      });
    });

    Object.keys(fields).forEach(function (k) {
      fields[k].addEventListener('input', function () { setError(''); });
      fields[k].addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          goNext();
        }
      });
    });

    var copyBtn = root.querySelector('[data-spp-copy]');
    if (copyBtn) copyBtn.addEventListener('click', function () { copySummary(copyBtn); });

    var linkBtn = root.querySelector('[data-spp-copylink]');
    if (linkBtn) {
      linkBtn.addEventListener('click', function () {
        var url = buildShareUrl();
        if (!url || !navigator.clipboard) return;
        navigator.clipboard.writeText(url).then(function () {
          flash(linkBtn, '✓ Link copied!', 'Result link copied to clipboard.');
        });
      });
    }

    var printBtn = root.querySelector('[data-spp-print]');
    if (printBtn) printBtn.addEventListener('click', function () { window.print(); });

    // "Change my inputs" keeps every answer; "Start over" clears them. Not the same.
    var changeBtn = root.querySelector('[data-spp-change]');
    if (changeBtn) changeBtn.addEventListener('click', function () { backToWizard(false); });
    var restartBtn = root.querySelector('[data-spp-restart]');
    if (restartBtn) restartBtn.addEventListener('click', function () { backToWizard(true); });

    renderStatic();
    showStep(1, false);
    restoreFromUrl();
  }

  for (var i = 0; i < roots.length; i++) mount(roots[i]);
})();
